// src/components/TicketFilters.jsx
import React, { useMemo } from "react";
import {
  Flex,
  Input,
  Select,
  Button,
  Box,
  Text,
  HStack,
} from "@chakra-ui/react";
import { FiPlus, FiUser, FiUsers } from "react-icons/fi";
import { useTicketConstants } from "../hooks/useTicketConstants";

const getDisplayName = (entity) => {
  if (!entity) return "";
  if (entity.name) return entity.name;

  return [entity.firstName, entity.lastName].filter(Boolean).join(" ");
};

const TicketFilters = ({
  searchTerm,
  setSearchTerm,
  statusFilter,
  setStatusFilter,
  priorityFilter,
  setPriorityFilter,
  severityFilter,
  setSeverityFilter,
  assignedToFilter,
  setAssignedToFilter,
  requestedByFilter,
  setRequestedByFilter,
  clientFilter,
  setClientFilter,
  itemsPerPage,
  setItemsPerPage,
  onNewTicket,
  allTickets = [],
  allEmployees = [],
  allClients = [],
  categoryFilter = "",
  setCategoryFilter,
  slaStatusFilter = "",
  setSlaStatusFilter,
  ageRangeFilter = "",
  setAgeRangeFilter,
  projectFilter = "",
  setProjectFilter,
  sprintFilter = "",
  setSprintFilter,
  milestoneFilter = "",
  setMilestoneFilter,
  ticketViewMode = "personal", // "personal" or "all"
  onToggleTicketViewMode, // function to toggle view mode
}) => {
  // Fetch ticket constants from server
  const { WORKFLOW_STATE, WORKFLOW_STATE_LABELS } = useTicketConstants();

  const assignedToOptions = useMemo(
    () =>
      allEmployees
        .map((employee) => ({
          label:
            getDisplayName(employee) || employee.employeeId || employee.email,
          value: employee._id,
        }))
        .filter((item) => item.value && item.label)
        .sort((a, b) => a.label.localeCompare(b.label)),
    [allEmployees],
  );

  const requestedByOptions = useMemo(
    () =>
      allEmployees
        .map((employee) => ({
          label:
            getDisplayName(employee) || employee.employeeId || employee.email,
          value: employee._id,
        }))
        .filter((item) => item.value && item.label)
        .sort((a, b) => a.label.localeCompare(b.label)),
    [allEmployees],
  );

  const clientOptions = useMemo(
    () =>
      allClients
        .map((client) => ({
          label: getDisplayName(client) || client.clientId || client.email,
          value: client._id,
        }))
        .filter((item) => item.value && item.label)
        .sort((a, b) => a.label.localeCompare(b.label)),
    [allClients],
  );

  const projectOptions = useMemo(() => {
    const map = new Map();
    allTickets.forEach((ticket) => {
      const project = ticket?.projectId;
      if (!project?._id) return;
      if (!map.has(project._id)) {
        map.set(project._id, {
          value: project._id,
          label: project.projectName || project.projectCode || project._id,
        });
      }
    });
    return Array.from(map.values()).sort((a, b) =>
      a.label.localeCompare(b.label),
    );
  }, [allTickets]);

  const sprintOptions = useMemo(() => {
    const map = new Map();
    allTickets.forEach((ticket) => {
      if (projectFilter && ticket?.projectId?._id !== projectFilter) return;
      const sprint = ticket?.sprintId;
      if (!sprint?._id) return;
      if (!map.has(sprint._id)) {
        map.set(sprint._id, {
          value: sprint._id,
          label: sprint.sprintName || sprint.sprintCode || sprint._id,
        });
      }
    });
    return Array.from(map.values()).sort((a, b) =>
      a.label.localeCompare(b.label),
    );
  }, [allTickets, projectFilter]);

  const milestoneOptions = useMemo(() => {
    const map = new Map();
    allTickets.forEach((ticket) => {
      if (projectFilter && ticket?.projectId?._id !== projectFilter) return;
      const milestone = ticket?.milestoneId;
      if (!milestone?._id) return;
      if (!map.has(milestone._id)) {
        map.set(milestone._id, {
          value: milestone._id,
          label: milestone.title || milestone.status || milestone._id,
        });
      }
    });
    return Array.from(map.values()).sort((a, b) =>
      a.label.localeCompare(b.label),
    );
  }, [allTickets, projectFilter]);

  const handleClearFilters = () => {
    setSearchTerm("");
    setStatusFilter("");
    setPriorityFilter("");
    if (typeof setSeverityFilter === "function") {
      setSeverityFilter("");
    }
    setAssignedToFilter("");
    setRequestedByFilter("");
    setClientFilter("");
    if (typeof setProjectFilter === "function") {
      setProjectFilter("");
    }
    if (typeof setSprintFilter === "function") {
      setSprintFilter("");
    }
    if (typeof setMilestoneFilter === "function") {
      setMilestoneFilter("");
    }
    if (typeof setCategoryFilter === "function") {
      setCategoryFilter("");
    }
    if (typeof setSlaStatusFilter === "function") {
      setSlaStatusFilter("");
    }
    if (typeof setAgeRangeFilter === "function") {
      setAgeRangeFilter("");
    }
    setItemsPerPage(10);
  };

  return (
    <Box
      w="100%"
      bg="white"
      border="1px solid"
      borderColor="gray.200"
      borderRadius="2xl"
      p={4}
      boxShadow="sm"
    >
      <HStack justify="space-between" mb={3} flexWrap="wrap" spacing={3}>
        <Text fontWeight="semibold" color="gray.700">
          Filter Queue
        </Text>
        <HStack spacing={2}>
          <Button
            size="sm"
            variant={ticketViewMode === "personal" ? "solid" : "outline"}
            colorScheme={ticketViewMode === "personal" ? "blue" : "gray"}
            onClick={onToggleTicketViewMode}
            aria-label={
              ticketViewMode === "personal"
                ? "Show my tickets"
                : "Show all tickets"
            }
            leftIcon={ticketViewMode === "personal" ? <FiUser /> : <FiUsers />}
          >
            {ticketViewMode === "personal" ? "My Tickets" : "All Tickets"}
          </Button>
          <Button size="sm" variant="ghost" onClick={handleClearFilters}>
            Clear filters
          </Button>
          <Button
            leftIcon={<FiPlus />}
            colorScheme="blue"
            onClick={onNewTicket}
            size="sm"
          >
            New Ticket
          </Button>
        </HStack>
      </HStack>
      <Flex gap={3} flexWrap="wrap" align="center">
        <Input
          placeholder="Search..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          flex={1}
          minW="220px"
          borderRadius="xl"
        />
        <Select
          placeholder="Status"
          w="140px"
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          borderRadius="xl"
        >
          <option value={WORKFLOW_STATE.OPEN}>
            {WORKFLOW_STATE_LABELS[WORKFLOW_STATE.OPEN]}
          </option>
          <option value={WORKFLOW_STATE.IN_PROGRESS}>
            {WORKFLOW_STATE_LABELS[WORKFLOW_STATE.IN_PROGRESS]}
          </option>
          <option value={WORKFLOW_STATE.ON_HOLD}>
            {WORKFLOW_STATE_LABELS[WORKFLOW_STATE.ON_HOLD]}
          </option>
          <option value={WORKFLOW_STATE.RESOLVED}>
            {WORKFLOW_STATE_LABELS[WORKFLOW_STATE.RESOLVED]}
          </option>
          <option value={WORKFLOW_STATE.CLOSED}>
            {WORKFLOW_STATE_LABELS[WORKFLOW_STATE.CLOSED]}
          </option>
        </Select>
        <Select
          placeholder="Priority"
          w="120px"
          value={priorityFilter}
          onChange={(e) => setPriorityFilter(e.target.value)}
          borderRadius="xl"
        >
          <option>Low</option>
          <option>Medium</option>
          <option>High</option>
          <option>Urgent</option>
        </Select>
        <Select
          placeholder="Severity"
          w="120px"
          value={severityFilter}
          onChange={(e) => setSeverityFilter(e.target.value)}
          borderRadius="xl"
        >
          <option value="Low">Low</option>
          <option value="Medium">Medium</option>
          <option value="High">High</option>
          <option value="Critical">Critical</option>
        </Select>
        <Select
          placeholder="SLA Status"
          w="140px"
          value={slaStatusFilter}
          onChange={(e) => setSlaStatusFilter(e.target.value)}
          borderRadius="xl"
        >
          <option value="breached">Breached</option>
          <option value="at-risk">At Risk</option>
          <option value="on-track">On Track</option>
        </Select>
        <Select
          placeholder="Age Range"
          w="140px"
          value={ageRangeFilter}
          onChange={(e) => setAgeRangeFilter(e.target.value)}
          borderRadius="xl"
        >
          <option value="0-7">0-7 days</option>
          <option value="7-30">7-30 days</option>
          <option value="30+">30+ days</option>
          <option value="60+">60+ days</option>
          <option value="90+">90+ days</option>
        </Select>
        <Select
          placeholder="Category"
          w="140px"
          value={categoryFilter}
          onChange={(e) => setCategoryFilter(e.target.value)}
          borderRadius="xl"
        >
          <option value="Access">Access</option>
          <option value="Hardware">Hardware</option>
          <option value="Software">Software</option>
          <option value="Network">Network</option>
        </Select>
        <Select
          placeholder="Assigned To"
          w="160px"
          value={assignedToFilter}
          onChange={(e) => setAssignedToFilter(e.target.value)}
          borderRadius="xl"
        >
          {assignedToOptions.map((item) => (
            <option key={item.value} value={item.value}>
              {item.label}
            </option>
          ))}
        </Select>
        <Select
          placeholder="Requested By"
          w="160px"
          value={requestedByFilter}
          onChange={(e) => setRequestedByFilter(e.target.value)}
          borderRadius="xl"
        >
          {requestedByOptions.map((item) => (
            <option key={item.value} value={item.value}>
              {item.label}
            </option>
          ))}
        </Select>
        <Select
          placeholder="Client"
          w="160px"
          value={clientFilter}
          onChange={(e) => setClientFilter(e.target.value)}
          borderRadius="xl"
        >
          {clientOptions.map((item) => (
            <option key={item.value} value={item.value}>
              {item.label}
            </option>
          ))}
        </Select>
        <Select
          placeholder="Project"
          w="180px"
          value={projectFilter}
          onChange={(e) => {
            setProjectFilter(e.target.value);
            setSprintFilter("");
            setMilestoneFilter("");
          }}
          borderRadius="xl"
        >
          {projectOptions.map((item) => (
            <option key={item.value} value={item.value}>
              {item.label}
            </option>
          ))}
        </Select>
        <Select
          placeholder="Sprint"
          w="170px"
          value={sprintFilter}
          onChange={(e) => setSprintFilter(e.target.value)}
          borderRadius="xl"
          isDisabled={!projectFilter}
        >
          {sprintOptions.map((item) => (
            <option key={item.value} value={item.value}>
              {item.label}
            </option>
          ))}
        </Select>
        <Select
          placeholder="Milestone"
          w="170px"
          value={milestoneFilter}
          onChange={(e) => setMilestoneFilter(e.target.value)}
          borderRadius="xl"
          isDisabled={!projectFilter}
        >
          {milestoneOptions.map((item) => (
            <option key={item.value} value={item.value}>
              {item.label}
            </option>
          ))}
        </Select>
        <Select
          w="110px"
          value={itemsPerPage}
          onChange={(e) => setItemsPerPage(Number(e.target.value))}
          borderRadius="xl"
        >
          <option value={5}>5</option>
          <option value={10}>10</option>
          <option value={25}>25</option>
          <option value={50}>50</option>
          <option value={100}>100</option>
        </Select>
      </Flex>
    </Box>
  );
};

export default TicketFilters;
